/**
 * Role Management Page
 *
 * Lists all role assignments for a tenant's users with assign and revoke actions.
 * Capability-gated to users with admin:roles capability.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Shield,
  Loader2,
  AlertCircle,
  RefreshCw,
  AlertTriangle,
  UserCog,
} from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { useAuthStore } from "../../../store/authStore";

import { CapabilityViewer } from "./CapabilityViewer";
import { RoleAssignmentDialog } from "./RoleAssignmentDialog";

// ============================================================================
// Types
// ============================================================================

/** Role assignment from API */
interface RoleAssignment {
  readonly userId: string;
  readonly tenantId: string;
  readonly role: string;
  readonly capabilities: readonly string[];
  readonly status: string;
  readonly email: string;
  readonly name: string;
  readonly userStatus: string;
  readonly updatedAt: string;
}

/** Assignment dialog target */
interface AssignmentTarget {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly currentRole: string;
}

// ============================================================================
// Constants
// ============================================================================

const ROLE_COLORS: Record<string, string> = {
  viewer: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
  editor: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  publisher: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  admin: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  suspended: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};

// ============================================================================
// Main Component
// ============================================================================

export function RoleManagementPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  if (!hasCapability("admin:roles")) {
    return <Navigate to="/studio" replace />;
  }

  return <RoleManagementContent />;
}

function RoleManagementContent(): ReactElement {
  const { tenantId } = useParams<{ tenantId: string }>();
  const queryClient = useQueryClient();

  const [assignTarget, setAssignTarget] = useState<AssignmentTarget | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<RoleAssignment | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["roles", tenantId],
    queryFn: async () => {
      const response = await apiRequest<RoleAssignment[]>(`/api/v1/tenants/${tenantId}/roles`);
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: Boolean(tenantId),
  });

  // ==========================================================================
  // Mutations
  // ==========================================================================

  const assignMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const response = await apiRequest<RoleAssignment>(`/api/v1/tenants/${tenantId}/roles`, {
        method: "POST",
        body: { userId, role },
      });
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles", tenantId] });
      setAssignTarget(null);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiRequest<{ success: boolean }>(
        `/api/v1/tenants/${tenantId}/roles/${userId}`,
        { method: "DELETE" }
      );
      if (!response.success) {
        throw new Error(response.error.message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles", tenantId] });
      setRevokeTarget(null);
    },
  });

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleAssign = useCallback(
    (userId: string, role: string): void => {
      assignMutation.mutate({ userId, role });
    },
    [assignMutation]
  );

  const handleConfirmRevoke = useCallback((): void => {
    if (!revokeTarget) return;
    revokeMutation.mutate(revokeTarget.userId);
  }, [revokeTarget, revokeMutation]);

  const handleOpenAssign = useCallback((assignment: RoleAssignment): void => {
    setAssignTarget({
      userId: assignment.userId,
      email: assignment.email,
      name: assignment.name,
      currentRole: assignment.role,
    });
  }, []);

  const handleToggleExpand = useCallback((userId: string): void => {
    setExpandedUser((prev) => (prev === userId ? null : userId));
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================

  if (isLoading) {
    return <LoadingState tenantId={tenantId} />;
  }

  if (isError) {
    return (
      <ErrorState
        tenantId={tenantId}
        message={error instanceof Error ? error.message : "Failed to load role assignments"}
        onRetry={refetch}
      />
    );
  }

  const assignments = data ?? [];

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6">
        <Link
          to={`/studio/admin/tenants/${tenantId}`}
          className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tenant
        </Link>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Shield className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Role Management</h1>
              <p className="mt-1 text-foreground-muted">
                Manage user roles and permissions for this tenant
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Role Assignments Table */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">User Roles ({assignments.length})</h2>

        {assignments.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="pb-3 pr-4 font-medium text-foreground-muted">User</th>
                  <th className="pb-3 pr-4 font-medium text-foreground-muted">Role</th>
                  <th className="pb-3 pr-4 font-medium text-foreground-muted">Status</th>
                  <th className="pb-3 pr-4 font-medium text-foreground-muted">Updated</th>
                  <th className="pb-3 font-medium text-foreground-muted">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => (
                  <RoleRow
                    key={assignment.userId}
                    assignment={assignment}
                    isExpanded={expandedUser === assignment.userId}
                    onToggleExpand={handleToggleExpand}
                    onAssign={handleOpenAssign}
                    onRevoke={setRevokeTarget}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assignment Dialog */}
      {assignTarget && (
        <RoleAssignmentDialog
          user={assignTarget}
          isLoading={assignMutation.isPending}
          {...(assignMutation.error instanceof Error
            ? { error: assignMutation.error.message }
            : {})}
          onSubmit={handleAssign}
          onCancel={() => {
            setAssignTarget(null);
            assignMutation.reset();
          }}
        />
      )}

      {/* Revoke Confirmation Dialog */}
      {revokeTarget && (
        <RevokeConfirmationDialog
          user={revokeTarget}
          isLoading={revokeMutation.isPending}
          onConfirm={handleConfirmRevoke}
          onCancel={() => setRevokeTarget(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Role Row Component
// ============================================================================

interface RoleRowProps {
  readonly assignment: RoleAssignment;
  readonly isExpanded: boolean;
  readonly onToggleExpand: (userId: string) => void;
  readonly onAssign: (assignment: RoleAssignment) => void;
  readonly onRevoke: (assignment: RoleAssignment) => void;
}

function RoleRow({
  assignment,
  isExpanded,
  onToggleExpand,
  onAssign,
  onRevoke,
}: RoleRowProps): ReactElement {
  return (
    <>
      <tr className="border-b border-border last:border-0">
        <td className="py-3 pr-4">
          <div>
            <p className="font-medium">{assignment.name}</p>
            <p className="text-xs text-foreground-muted">{assignment.email}</p>
          </div>
        </td>
        <td className="py-3 pr-4">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
              ROLE_COLORS[assignment.role] ?? ROLE_COLORS["viewer"]
            }`}
          >
            {assignment.role}
          </span>
        </td>
        <td className="py-3 pr-4">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
              STATUS_COLORS[assignment.status] ?? STATUS_COLORS["active"]
            }`}
          >
            {assignment.status}
          </span>
        </td>
        <td className="py-3 pr-4 text-foreground-muted">
          {new Date(assignment.updatedAt).toLocaleDateString()}
        </td>
        <td className="py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onToggleExpand(assignment.userId)}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-foreground-muted hover:bg-background hover:text-foreground"
              title="View capabilities"
            >
              <Shield className="h-3.5 w-3.5" />
              {isExpanded ? "Hide" : "Capabilities"}
            </button>
            <button
              onClick={() => onAssign(assignment)}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
              title="Change role"
            >
              <UserCog className="h-3.5 w-3.5" />
              Change
            </button>
            {assignment.role !== "viewer" && (
              <button
                onClick={() => onRevoke(assignment)}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-error hover:bg-error/10"
                title="Revoke role (reset to viewer)"
              >
                Revoke
              </button>
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-border">
          <td colSpan={5} className="py-3 px-4 bg-background-alt">
            <CapabilityViewer role={assignment.role} />
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================================
// Revoke Confirmation Dialog
// ============================================================================

interface RevokeConfirmationDialogProps {
  readonly user: RoleAssignment;
  readonly isLoading: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function RevokeConfirmationDialog({
  user,
  isLoading,
  onConfirm,
  onCancel,
}: RevokeConfirmationDialogProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-error/10 text-error">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold">Revoke Role</h2>
        </div>

        <p className="text-foreground-muted mb-2">Are you sure you want to revoke the role for:</p>
        <div className="rounded-md border border-border bg-background-alt p-3 mb-4">
          <p className="font-medium text-sm">{user.name}</p>
          <p className="text-xs text-foreground-muted">{user.email}</p>
          <p className="text-xs text-foreground-muted mt-1">
            Current role: <span className="font-medium capitalize">{user.role}</span>
          </p>
        </div>
        <p className="text-sm text-foreground-muted mb-6">
          This will reset the user's role to <strong>viewer</strong> (read-only access). They will
          lose all elevated permissions immediately.
        </p>

        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={isLoading} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={isLoading} className="btn btn-error">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Revoking...
              </>
            ) : (
              "Revoke Role"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// State Components
// ============================================================================

function EmptyState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Shield className="h-8 w-8" />
      <p className="mt-2">No users found in this tenant</p>
      <p className="text-xs mt-1">Add users to the tenant to manage their roles.</p>
    </div>
  );
}

interface LoadingStateProps {
  readonly tenantId: string | undefined;
}

function LoadingState({ tenantId }: LoadingStateProps): ReactElement {
  return (
    <div className="p-6">
      <Link
        to={`/studio/admin/tenants/${tenantId}`}
        className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Tenant
      </Link>
      <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="mt-4">Loading role assignments...</p>
      </div>
    </div>
  );
}

interface ErrorStateProps {
  readonly tenantId: string | undefined;
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ tenantId, message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="p-6">
      <Link
        to={`/studio/admin/tenants/${tenantId}`}
        className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Tenant
      </Link>
      <div className="flex flex-col items-center justify-center py-12">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
          <AlertCircle className="h-6 w-6 text-error" />
        </div>
        <p className="mt-4 text-foreground-muted">{message}</p>
        <button onClick={onRetry} className="btn btn-secondary mt-4">
          <RefreshCw className="mr-2 h-4 w-4" />
          Try again
        </button>
      </div>
    </div>
  );
}
